import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

type QualityLevel = 0 | 1 | 2;
type ConfidenceLevel = 0 | 1 | 2;

interface CellData {
  quality: QualityLevel;
  confidence: ConfidenceLevel;
}

function qualityLabel(q: QualityLevel): string {
  if (q === 0) return '表现优秀';
  if (q === 1) return '发展过程中';
  return '需要进一步支持';
}

function confidenceLabel(c: ConfidenceLevel): string {
  if (c === 0) return '证据充分';
  if (c === 1) return '证据一般';
  return '样本不足 · 低置信度';
}

function cellTooltip(q: QualityLevel, c: ConfidenceLevel): string {
  return `${qualityLabel(q)} · ${confidenceLabel(c)}`;
}

function qualityColorClass(q: QualityLevel, color: 'primary' | 'accent'): string {
  if (color === 'accent') {
    if (q === 0) return 'bg-accent-500';
    if (q === 1) return 'bg-accent-300';
    return 'bg-accent-100';
  }
  if (q === 0) return 'bg-primary-500';
  if (q === 1) return 'bg-primary-300';
  return 'bg-primary-100';
}

function confidenceCellClass(c: ConfidenceLevel): string {
  if (c === 0) return '';
  if (c === 1) return 'opacity-[0.55]';
  return 'opacity-[0.28] border-2 border-dashed border-foreground-400/50';
}

function qualityBadgeColor(q: QualityLevel, color: 'primary' | 'accent'): string {
  if (color === 'accent') {
    if (q === 0) return 'bg-accent-500';
    if (q === 1) return 'bg-accent-300';
    return 'bg-accent-100';
  }
  if (q === 0) return 'bg-primary-500';
  if (q === 1) return 'bg-primary-300';
  return 'bg-primary-100';
}

// ─── Grid Data ──────────────────────────────────────────────────────────

const teacherGrid: CellData[] = [
  { quality: 0, confidence: 0 }, { quality: 0, confidence: 0 }, { quality: 0, confidence: 1 }, { quality: 0, confidence: 0 }, { quality: 0, confidence: 0 },
  { quality: 0, confidence: 1 }, { quality: 0, confidence: 2 }, { quality: 0, confidence: 0 }, { quality: 0, confidence: 1 }, { quality: 0, confidence: 2 },
  { quality: 1, confidence: 0 }, { quality: 0, confidence: 0 }, { quality: 1, confidence: 0 }, { quality: 1, confidence: 1 }, { quality: 1, confidence: 0 },
  { quality: 1, confidence: 2 }, { quality: 2, confidence: 2 }, { quality: 1, confidence: 1 }, { quality: 1, confidence: 0 }, { quality: 2, confidence: 2 },
];

const schoolGrid: CellData[] = [
  { quality: 0, confidence: 0 }, { quality: 0, confidence: 0 }, { quality: 0, confidence: 0 }, { quality: 1, confidence: 0 }, { quality: 0, confidence: 0 },
  { quality: 1, confidence: 0 }, { quality: 1, confidence: 0 }, { quality: 2, confidence: 1 }, { quality: 1, confidence: 1 }, { quality: 1, confidence: 0 },
  { quality: 0, confidence: 1 }, { quality: 1, confidence: 0 }, { quality: 1, confidence: 0 }, { quality: 2, confidence: 1 }, { quality: 1, confidence: 1 },
  { quality: 2, confidence: 2 }, { quality: 2, confidence: 1 }, { quality: 1, confidence: 2 }, { quality: 2, confidence: 2 }, { quality: 2, confidence: 1 },
];

const provinceGrid: CellData[] = [
  { quality: 0, confidence: 0 }, { quality: 0, confidence: 0 }, { quality: 0, confidence: 1 }, { quality: 0, confidence: 0 }, { quality: 1, confidence: 1 },
  { quality: 1, confidence: 0 }, { quality: 1, confidence: 0 }, { quality: 2, confidence: 2 }, { quality: 1, confidence: 2 }, { quality: 1, confidence: 0 },
  { quality: 0, confidence: 1 }, { quality: 1, confidence: 1 }, { quality: 1, confidence: 0 }, { quality: 2, confidence: 2 }, { quality: 2, confidence: 2 },
  { quality: 2, confidence: 1 }, { quality: 2, confidence: 2 }, { quality: 2, confidence: 2 }, { quality: 2, confidence: 0 }, { quality: 2, confidence: 1 },
];

const heatmaps = [
  {
    title: '一师一档',
    subtitle: '教学维度 × 多节课程',
    icon: 'ri-user-line',
    color: 'primary' as const,
    gridData: teacherGrid,
  },
  {
    title: '一校一图',
    subtitle: '课程主题 × 评价维度',
    icon: 'ri-building-line',
    color: 'accent' as const,
    gridData: schoolGrid,
  },
  {
    title: '一省一图',
    subtitle: '地区/学校 × 质量维度',
    icon: 'ri-map-2-line',
    color: 'primary' as const,
    gridData: provinceGrid,
  },
];

// ─── Confidence Legend Sample Cells ─────────────────────────────────────

const confidenceSamples: { label: string; confidence: ConfidenceLevel; quality: QualityLevel }[] = [
  { label: '证据充分 — 实色', confidence: 0, quality: 0 },
  { label: '证据一般 — 半透明', confidence: 1, quality: 0 },
  { label: '样本不足 — 虚线 · 低置信度', confidence: 2, quality: 0 },
];

// ─── Component ──────────────────────────────────────────────────────────

function MiniHeatmapCell({ cell, color }: { cell: CellData; color: 'primary' | 'accent' }) {
  const hasDashed = cell.confidence === 2;

  return (
    <div className="relative" title={cellTooltip(cell.quality, cell.confidence)}>
      <div
        className={`aspect-square rounded-sm transition-colors duration-200 ${qualityColorClass(cell.quality, color)} ${confidenceCellClass(cell.confidence)}`}
      />
      {hasDashed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-[8px] font-body font-bold text-foreground-950/60 leading-none">!</span>
        </div>
      )}
    </div>
  );
}

// ─── Page Component ─────────────────────────────────────────────────────

export default function P74CourseCoverage({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-3 md:py-4">
        {/* ── Title ── */}
        <div ref={titleRef} className="text-center mb-3 md:mb-4">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />热力覆盖图
          </span>
          <h2 className={`font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            颜色深浅看质量，透明度看<span className="text-gold-600">置信度</span>
          </h2>
          <p className={`text-xs md:text-sm text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            热力图同时表达两个维度——颜色深浅表示质量水平；透明度与边框表示证据充分度，避免将"表现好坏"和"数据多少"混为一谈
          </p>
        </div>

        {/* ── Two-axis Legend ── */}
        <div className={`max-w-4xl mx-auto mb-3 transition-all duration-1000 delay-250 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="p-3 md:p-4 glass-liquid rounded-xl">
            <div className="flex flex-col md:flex-row gap-4 md:gap-8 justify-center">
              {/* Quality axis */}
              <div className="flex-1 max-w-xs mx-auto md:mx-0">
                <p className="text-[10px] font-body font-bold text-foreground-950 uppercase tracking-wider mb-2 text-center">颜色深浅 → 质量水平</p>
                <div className="flex items-center justify-center gap-3">
                  {([0, 1, 2] as QualityLevel[]).map((q) => (
                    <div key={q} className="flex items-center gap-1.5">
                      <span className={`w-4 h-4 rounded-sm ${qualityBadgeColor(q, 'primary')}`} />
                      <span className="text-[11px] font-body text-foreground-950 whitespace-nowrap">{qualityLabel(q)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px bg-foreground-200/30 self-stretch" />
              <div className="md:hidden w-full h-px bg-foreground-200/30" />

              {/* Confidence axis */}
              <div className="flex-1 max-w-xs mx-auto md:mx-0">
                <p className="text-[10px] font-body font-bold text-foreground-950 uppercase tracking-wider mb-2 text-center">透明度 · 边框 → 证据充分度</p>
                <div className="flex items-center justify-center gap-3">
                  {confidenceSamples.map((s) => (
                    <div key={s.label} className="flex items-center gap-1.5">
                      <div className="relative w-4 h-4">
                        <div className={`w-4 h-4 rounded-sm bg-primary-500 ${s.confidence === 1 ? 'opacity-[0.55]' : ''} ${s.confidence === 2 ? 'opacity-[0.28] border-2 border-dashed border-foreground-400/50' : ''}`} />
                        {s.confidence === 2 && (
                          <span className="absolute inset-0 flex items-center justify-center text-[7px] font-body font-bold text-foreground-950/60 leading-none">!</span>
                        )}
                      </div>
                      <span className="text-[11px] font-body text-foreground-950 whitespace-nowrap">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Three Heatmap Cards ── */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {heatmaps.map((hm, hmi) => (
            <div
              key={hm.title}
              className="p-4 md:p-5 glass-liquid rounded-xl"
              style={{ transitionDelay: `${300 + hmi * 150}ms` }}
            >
              {/* Card header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl ${hm.color === 'accent' ? 'bg-accent-100/80 border border-accent-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                  <i className={`${hm.icon} text-lg ${hm.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`} />
                </div>
                <div>
                  <h4 className="font-heading text-sm font-bold text-foreground-950">{hm.title}</h4>
                  <p className="text-xs text-foreground-950 font-body">{hm.subtitle}</p>
                </div>
              </div>

              {/* Per-card condensed legend */}
              <div className="flex items-center gap-2 mb-3 text-[10px] font-body text-foreground-950 justify-center flex-wrap">
                <span>深=优</span>
                <span className="text-foreground-950">·</span>
                <span>中=发展中</span>
                <span className="text-foreground-950">·</span>
                <span>浅=需支持</span>
                <span className="text-foreground-950 mx-0.5">|</span>
                <span>实色=证据充分</span>
                <span className="text-foreground-950">·</span>
                <span>半透明=证据一般</span>
                <span className="text-foreground-950">·</span>
                <span>虚线=样本不足</span>
              </div>

              {/* Mini heatmap grid */}
              <div className="rounded-lg overflow-hidden">
                <div className="grid grid-cols-5 gap-[2px] bg-background-200/20 p-[2px]">
                  {hm.gridData.map((cell, ci) => (
                    <MiniHeatmapCell key={ci} cell={cell} color={hm.color} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Bottom Conclusion ── */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 md:p-5 glass-liquid rounded-xl">
            <p className="text-xs md:text-sm text-foreground-950 font-body leading-relaxed">
              热力图不是用颜色替代专业判断，而是帮助管理者快速定位需要关注的位置。<strong className="text-gold-700">深色但虚线边框的格子</strong>提示"这里表现可能不错，但数据不够，需要补充证据"；<strong className="text-gold-700">浅色实色格子</strong>则明确告知"此处确实需要加强"。管理者可从区域下钻到学校，从学校下钻到课程和教师，最终回到某一节课的具体证据。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}